import React from 'react';

import type { PersonalisationSlot } from '../../context/personalisation-typedefs';
import PersonalisationAlphanumericSlot from './PersonalisationAlphanumericSlot.jsx';
import { PersonalisationImageUploadSlot } from './PersonalisationImageUploadSlot.jsx';
import { OptionSelection } from './OptionSelection';
import { useGroupedSlotViewModel } from './useGroupedSlotViewModel';
import { getFontDisplayName } from '../../utilities/getFontDisplayName.js';

import stepStyles from './personalisation-step.module.css';
import slotStyles from './personalisation-slot.module.css';
import inputStyles from './personalisation-inputs.module.css';
import gridStyles from './personalisation-grid.module.css';

interface PersonalisationGroupedSlotProps {
  slots: PersonalisationSlot[];
}

/**
 * PersonalisationGroupedSlot — renders slots when step slotMode is GROUPED.
 * Text and image upload only. Choose option and font picker appear together below
 * all input lines once the customer has started (typed or uploaded) and apply to
 * every line.
 */
export default function PersonalisationGroupedSlot({ slots }: PersonalisationGroupedSlotProps): React.JSX.Element {
  const {
    inputLines,
    activeGroupName,
    optionSelectionChoices,
    shouldShowChooseOption,
    isImageUploadStep,
    hasUserProvidedContent,
    parsedAllowedFontsForGroup,
    hasFontOptionsForGroup,
    selectedFontForGroup,
    fontPreviewLines,
    handleGroupFontChange,
    handleInputLineChange,
    handleGroupChange,
    handleImageUploadedInGroup
  } = useGroupedSlotViewModel({ slots });

  const optionColumns = Math.min(Math.max(optionSelectionChoices.length, 2), 3);
  const chooseOptionGroupName = `group-selection-${inputLines[0]?.slot?.id ?? 'grouped'}`;
  const isDisabled = inputLines.some((line) => line.slot.disabled);

  // Font options are only relevant for text slots, not image upload.
  const shouldShowFont = hasFontOptionsForGroup && !isImageUploadStep;

  // Both font and choose-option sit together once the customer has started filling in content.
  const shouldShowStylingPanel = hasUserProvidedContent;

  // For text steps: key=activeGroupName causes a remount + CSS fade animation on every group switch.
  // For image upload steps: a stable key keeps the PhotoEditor mounted across group changes so the
  // same File reference flows through without triggering re-initialisation of the imageUrl state.
  // This eliminates the blank-preview flash from PhotoEditor's useEffect-deferred objectURL.
  const inputLinesWrapperKey = isImageUploadStep ? 'stable' : activeGroupName;

  return (
    <div className={stepStyles['personalisation__step']}>
      <div
        key={inputLinesWrapperKey}
        className={isImageUploadStep ? undefined : slotStyles['personalisation__slot-group-content']}
      >
        {inputLines.map(({ inputLineIndex, slot, inputValue }) => (
          <div key={`line-${inputLineIndex}`} className={slotStyles['personalisation__slot']}>
            {slot.slotType === 'ALPHANUMERIC' && (
              <PersonalisationAlphanumericSlot
                slot={slot.id}
                value={slot}
                inputValue={inputValue}
                suppressFontSelection
                onChange={(id: string, newValue: unknown) => handleInputLineChange(inputLineIndex, id, newValue)}
              />
            )}

            {slot.slotType === 'IMAGE_UPLOAD' && (
              <PersonalisationImageUploadSlot
                slot={slot as PersonalisationSlot & { handle: string; stepId: string }}
                hideMaskZoneButtons
                groupLineItemPropertyKey={activeGroupName}
                onImageSaved={(imageData) => handleImageUploadedInGroup(inputLineIndex, imageData)}
              />
            )}

            {isImageUploadStep &&
              !hasUserProvidedContent &&
              optionSelectionChoices.length > 1 &&
              inputLineIndex === 0 && (
                <p className={inputStyles['personalisation__input-helper-text'] + ' body-xs'}>
                  Upload your image to choose a colour or style.
                </p>
              )}
          </div>
        ))}
      </div>

      {shouldShowStylingPanel && (shouldShowFont || shouldShowChooseOption) && (
        <div className={slotStyles['personalisation__slot']}>
          {shouldShowFont && (
            <fieldset className={inputStyles['personalisation__input-font-selection']}>
              <legend
                className={
                  inputStyles['personalisation__input-font-selection-label'] + ' body-xs body-uppercase font-medium'
                }
              >
                {parsedAllowedFontsForGroup.length > 1 ? 'Choose font' : 'Font preview'}
              </legend>
              <ul
                className={
                  gridStyles['personalisation__selection-grid'] +
                  ' ' +
                  gridStyles['personalisation__selection-grid--3-column']
                }
                role="list"
              >
                {parsedAllowedFontsForGroup.map((fontName, index) => (
                  <li key={fontName} className={inputStyles['personalisation__input-font-selection-card']}>
                    <input
                      disabled={isDisabled}
                      type="radio"
                      id={`${chooseOptionGroupName}-font-${index}`}
                      name={`font-selection-${chooseOptionGroupName}`}
                      value={fontName}
                      checked={selectedFontForGroup === fontName}
                      onChange={() => handleGroupFontChange(fontName)}
                      className={inputStyles['personalisation__input-font-selection-hidden-input']}
                    />
                    <label
                      htmlFor={`${chooseOptionGroupName}-font-${index}`}
                      className={inputStyles['personalisation__input-font-selection-option-label']}
                    >
                      <div className={inputStyles['personalisation__input-font-selection-font-preview-lines']}>
                        {fontPreviewLines.map((lineText, lineIndex) => (
                          <p
                            key={lineIndex}
                            className={
                              inputStyles['personalisation__input-font-selection-font-preview'] +
                              ' ' +
                              inputStyles[
                                `personalisation__input-font-selection-font-preview--${fontName.replace(/\s+/g, '-')}`
                              ] +
                              ' body-lg'
                            }
                          >
                            {lineText}
                          </p>
                        ))}
                      </div>
                      <p className={inputStyles['personalisation__input-font-selection-font-name'] + ' body-xs'}>
                        {getFontDisplayName({ fontName })}
                      </p>
                    </label>
                  </li>
                ))}
              </ul>
            </fieldset>
          )}

          {shouldShowChooseOption && (
            <OptionSelection
              name={chooseOptionGroupName}
              legend="Choose option"
              options={optionSelectionChoices}
              selected={activeGroupName}
              onChange={handleGroupChange}
              disabled={isDisabled}
              columns={optionColumns}
            />
          )}
        </div>
      )}
    </div>
  );
}
